/**
 * The native material library's data and its one shared formatter.
 *
 * Both live outside the components that use them so `CourseMaterialLibrary` and
 * `CourseMaterialsPanel` can share one query key and one rounding of a byte
 * count without either file exporting a non-component.
 */
import { useQuery } from "@tanstack/react-query";

import { courseMaterialsApi } from "@/lib/api/courseMaterials";

/**
 * `enabled` is not an authority boundary — migration 063 narrows `lms_material`
 * itself, so an enrolled non-editor reads only placement-published rows
 * whoever asks. It is a surface declining to request a list it has no controls
 * for.
 */
export function useCourseMaterials(courseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["course-materials", courseId],
    queryFn: () => courseMaterialsApi.getMaterials(courseId),
    enabled,
  });
}

/** Shown identically on the instructor's library row and the learner's reference. */
export function formatFileSize(bytes: number | null): string | null {
  if (bytes == null || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}
